import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Calendar, Users, CaretLeft, CaretRight } from "phosphor-react";
import { Link, useNavigate } from "react-router-dom";
import { getImageUrl } from "../../utils/imageUtils.js";

/* ─── STATUS HELPERS ─────────────────────────────────────────────────────── */

const STATUS_STYLE = {
  past: { bg: "#94a3b8", ring: "#e2e8f0" },
  live: { bg: "#166534", ring: "#bbf7d0" },
  upcoming: { bg: "#f97316", ring: "#fed7aa" },
};

function normalizeStatus(rawStatus) {
  let status =
    rawStatus === "predefined" ? "upcoming" : rawStatus || "upcoming";
  if (status === "current") status = "live";
  if (status === "completed") status = "past";
  return status;
}

const GEOMETRY = {
  desktop: {
    spacingX: 250,
    riseY: 14,
    scaleStep: 0.13,
    rotateStep: 7,
    maxRotate: 15,
    visible: 2,
    cardW: 280,
    cardH: 360,
    centerCardW: 340,
  },
  tablet: {
    spacingX: 175,
    riseY: 11,
    scaleStep: 0.15,
    rotateStep: 8,
    maxRotate: 15,
    visible: 2,
    cardW: 210,
    cardH: 300,
    centerCardW: 250,
  },
  mobile: {
    spacingX: 120,
    riseY: 8,
    scaleStep: 0.16,
    rotateStep: 9,
    maxRotate: 15,
    visible: 1,
    cardW: 160,
    cardH: 230,
    centerCardW: 195,
  },
};

function useBreakpoint() {
  const [bp, setBp] = useState("desktop");
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setBp(w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop");
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  return bp;
}

export default function EventCarousel({ events, language, onSelect, labels }) {
  const total = events.length;
  const navigate = useNavigate();
  const liveIdx = events.findIndex(
    (ev) => normalizeStatus(ev.status) === "live",
  );
  const [index, setIndex] = useState(liveIdx !== -1 ? liveIdx : 0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const bp = useBreakpoint();
  const geo = GEOMETRY[bp];

  const goTo = useCallback(
    (i) => {
      if (total === 0) return;
      setIndex(((i % total) + total) % total);
    },
    [total],
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // autoplay, 4s, paused on hover/focus
  useEffect(() => {
    if (paused || total <= 1) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % total), 4000);
    return () => clearInterval(id);
  }, [paused, total]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") next();
    if (e.key === "ArrowLeft") prev();
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) dx < 0 ? next() : prev();
    touchStartX.current = null;
  };

  if (total === 0) return null;

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={labels.eyebrow || "Events"}
      tabIndex={0}
      className="relative mx-auto select-none outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A3A] focus-visible:ring-offset-4 rounded-3xl"
      style={{ height: geo.cardH + 90, maxWidth: 1120, perspective: 1600 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={handleKeyDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {events.map((ev, i) => {
        let diff = i - index;
        if (diff > total / 2) diff -= total;
        if (diff < -total / 2) diff += total;

        if (Math.abs(diff) > geo.visible) return null;

        const isCenter = diff === 0;
        const x = diff * geo.spacingX;
        const y = Math.abs(diff) * geo.riseY;
        const scale = Math.max(1 - Math.abs(diff) * geo.scaleStep, 0.62);
        const rotateY = Math.max(
          Math.min(-diff * geo.rotateStep, geo.maxRotate),
          -geo.maxRotate,
        );
        const opacity = Math.max(1 - Math.abs(diff) * 0.32, 0);
        const zIndex = 100 - Math.abs(diff);

        const status = normalizeStatus(ev.status);
        const sc = STATUS_STYLE[status] || STATUS_STYLE.upcoming;
        const statusLabel = labels[status] || status;

        const dateStr = ev.date
          ? new Date(ev.date).toLocaleDateString(
              language === "ne" ? "en-NP" : "en-US",
              {
                month: "short",
                day: "numeric",
                year: "numeric",
              },
            )
          : "";

        return (
          <motion.div
            key={ev._id || i}
            className="absolute top-1/2 left-1/2 cursor-pointer"
            style={{ zIndex, width: isCenter ? geo.centerCardW : geo.cardW }}
            initial={false}
            animate={{
              x: `calc(-50% + ${x}px)`,
              y: `calc(-50% + ${y}px)`,
              scale,
              rotateY,
              opacity,
            }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 28,
              mass: 0.9,
            }}
            onClick={() => (isCenter ? onSelect?.(ev) : goTo(i))}
          >
            <div
              className="overflow-hidden rounded-[24px] bg-white transition-shadow duration-300"
              style={{
                boxShadow: isCenter
                  ? `0 28px 56px -14px ${sc.bg}35, 0 10px 26px rgba(26,24,21,0.08)`
                  : "0 10px 26px rgba(26,24,21,0.07)",
                border: `1px solid ${isCenter ? sc.ring : "#EEECEA"}`,
              }}
            >
              <div
                className="relative"
                style={{
                  height: isCenter ? geo.cardH * 0.56 : geo.cardH * 0.64,
                }}
              >
                {ev.imageUrl ? (
                  <img
                    src={getImageUrl(ev.imageUrl)}
                    alt={ev.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-[#EBF5ED] to-[#DCEEDF]" />
                )}
              <span
  className="absolute top-3 left-3 inline-flex items-center justify-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-extrabold uppercase tracking-wide text-white text-center"
  style={{ background: sc.bg, minWidth: 72 }}
>
  {status === "live" && (
    <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
  )}
  {statusLabel}
</span>
              </div>

              <div className={isCenter ? "p-5" : "p-3.5"}>
                <h3
                  className={`font-extrabold text-[#1A1815] line-clamp-2 ${
                    isCenter ? "text-base" : "text-xs"
                  }`}
                >
                  {ev.title}
                </h3>

                {isCenter && (
                  <>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs font-semibold text-gray-500">
                      {dateStr && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={13} /> {dateStr}
                        </span>
                      )}
                      {ev.organizer && (
                        <span className="inline-flex items-center gap-1">
                          <Users size={13} style={{ color: "rgb(20 83 45)" }} />
                          {ev.organizer}
                        </span>
                      )}
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/events/${ev._id}`);
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#2D7A3A] px-3.5 py-2 text-xs font-bold text-white transition hover:bg-[#1E5528]"
                    >
                      {labels.viewDetails || "View Details"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}

      {total > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous event"
            onClick={prev}
            className="absolute left-0 top-1/2 z-[200] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[#EEECEA] bg-white/90 text-[#1A1815] shadow-md backdrop-blur transition hover:bg-white sm:left-2"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
          <button
            type="button"
            aria-label="Next event"
            onClick={next}
            className="absolute right-0 top-1/2 z-[200] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[#EEECEA] bg-white/90 text-[#1A1815] shadow-md backdrop-blur transition hover:bg-white sm:right-2"
          >
            <CaretRight size={16} weight="bold" />
          </button>

          <div className="absolute -bottom-1 left-1/2 flex -translate-x-1/2 gap-2">
            {events.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to event ${i + 1}`}
                onClick={() => goTo(i)}
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: i === index ? 22 : 8,
                  background: i === index ? "#166534" : "#d4d4d4",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}